import { Location } from '@angular/common';
import { environment } from '../../environments/environment';

/**
 * @example
 * // returns 'hidden'
 * getCookie('dashboard_alert_12')
 *
 * @param name searched key
 * @returns cookie value
 */
export function getCookie(name: string): string {
    let cValue = ' ' + document.cookie;
    let cStart = cValue.indexOf(' ' + name + '=');
    if (cStart === -1) {
        cValue = null;
    } else {
        cStart = cValue.indexOf('=', cStart) + 1;
        let cEnd = cValue.indexOf(';', cStart);
        if (cEnd === -1) {
            cEnd = cValue.length;
        }
        cValue = unescape(cValue.substring(cStart, cEnd));
    }
    return cValue;
}

/**
 * @returns Deletes cookie key by name
 */
export function deleteCookie(name: string): void {
    if (getCookie(name)) {
        document.cookie = name + '=' + ';expires=Thu, 01 Jan 1970 00:00:01 GMT';
    }
}

/**
 * @returns Sets cookie key by nameand date
 */
export function setCookie(name: string, value: string): void {
    const expires = '; expires=session';
    document.cookie = name + '=' + (value || '') + expires + '; path=/; domain=' + environment.domain;
}

/**
 * @returns Operating system of client
 */
export function getOS(): 'macos' | 'ios' | 'windows' | 'android' | 'linux' {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const macosPlatforms = /(macintosh|macintel|macppc|mac68k|macos)/i;
    const windowsPlatforms = /(win32|win64|windows|wince)/i;
    const iosPlatforms = /(iphone|ipad|ipod)/i;

    if (macosPlatforms.test(userAgent)) {
        return 'macos';
    } else if (iosPlatforms.test(userAgent)) {
        return 'ios';
    } else if (windowsPlatforms.test(userAgent)) {
        return 'windows';
    } else if (/android/.test(userAgent)) {
        return 'android';
    } else if (/linux/.test(userAgent)) {
        return 'linux';
    }
}

/**
 * @returns Safe localStorage object, because Safari in private mode throws an error
 */
export const localStorageSafe = {
    getItem(key: string): string {
        try {
            return localStorage.getItem(key);
        } catch (e) {
            // localStorage does not exist, get a new browser !
        }
    },
    setItem(key: string, value: string): void {
        try {
            localStorage.setItem(key, value);
        } catch (e) {
            // localStorage does not exist, get a new browser !
        }
    },
    removeItem(key: string): void {
        try {
            localStorage.removeItem(key);
        } catch (e) {
            // localStorage does not exist, get a new browser !
        }
    }
};

/**
 * @returns string converted from camelCase to snake_case
 */
export function camelToSnakeCase(str: string): string {
    return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
}

/**
 * @returns string converted from snake_case to camelCase
 */
export function snakeToCamelCase(str: string): string {
    return str.replace(/([-_][a-z])/g, group => group.toUpperCase().replace('-', '').replace('_', ''));
}

/**
 * @returns decoded jwt token object
 */
export function parseJwtToken(): any {
    const base64Url = getCookie('token').split('.')[1];
    const base64 = base64Url
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    // Nutné doplnit padding "="
    const padded = base64.padEnd(base64.length + (4 - base64.length % 4) % 4, '=');

    // Dekódovat base64 na UTF-8
    const jsonPayload = decodeURIComponent(
        atob(padded)
            .split('')
            .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
            .join('')
    );

    return JSON.parse(jsonPayload);
}

/**
 * @ waits for element to be rendered and then scrolls to it
 */
export function scrollToElementAfterRender(elementID: string, location: Location): void {
    if (elementID) {
        let intervalID = null;
        intervalID = setInterval(() => {
            const element = document.getElementById(elementID);
            if (element) {
                clearInterval(intervalID);
                const headerOffset = 100; // navbar offset
                const elementPosition = element.getBoundingClientRect().top;
                const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
                location.replaceState(location.path(false));
            }
        });
    }
}

/**
 * @ Checks whether the element is visible or not
 */
export function isElementInView(container: HTMLElement, element: HTMLElement): boolean {
    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    return (
        elementRect.top >= containerRect.top &&
        elementRect.bottom <= containerRect.bottom
    );
}
